import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RobotOutlined, WarningOutlined } from '@ant-design/icons';

import { PageHeader } from '../../../components/shared';
import { Badge, Button, Select } from '../../../components/ui';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { HopXacNhan } from '../components/chung/HopXacNhan';
import { LoiVan } from '../components/chung/LoiVan';
import { GoiY } from '../components/hoi/CauHoiTiep';
import { ChonGiong } from '../components/hoi/ChonGiong';
import { DanhSachDoan, type MucDoan } from '../components/hoi/DanhSachDoan';
import { KhoiSoLieuDaLuu } from '../components/hoi/KhoiSoLieuDaLuu';
import { BongHoi, LuotHoi } from '../components/hoi/LuotHoi';
import { ThanhSoan } from '../components/hoi/ThanhSoan';
import { ghiNho, ghiNhoDs, nho, nhoDs } from '../hooks/ghi-nho';
import { useCoNghe, useDocTo, useNghe } from '../hooks/giong';
import { useDanhSachGiong, useDanhSachNguon, useSucKhoeTroLy } from '../hooks/useChung';
import { useMoTaLoi } from '../hooks/useMoTaLoi';
import {
  useGhiHoiThoai,
  useHoiThoaiTheoNguon,
  useLuotHoi,
  useMoHoiThoai,
  useXoaHoiThoai,
  type TuyChonLuotHoi,
} from '../hooks/useHoi';
import { goiSoLieu, lichSuTuTinNhan, nguonDemHoi } from '../nghiep-vu/hoi';
import { tenCongKhaiMacDinh } from '../nghiep-vu/ten-model';
import type { LuotLichSu } from '../types';

/**
 * Lựa chọn nguồn của màn này. `v2` vì bản trước lưu MỘT mã trần ở khoá cũ — xem `nhoDs`. Khoá cũ không bao giờ bị ghi
 * đè, nên một bản lùi vẫn chạy.
 */
const KHOA_NGUON = 'ask.nguon.v2';
const KHOA_NGUON_CU = 'ask.nguon';

/**
 * Lỗi đọc danh sách nguồn — vẽ RIÊNG, không gộp với trạng thái rỗng: cả 403 lẫn "chưa khai nguồn nào" từng hiện y hệt
 * nhau, và người dùng đi khai nguồn mới — việc không liên quan gì tới nguyên nhân.
 */
function LoiNguon({ loi, onThuLai }: { loi: unknown; onThuLai: () => void }) {
  const { t } = useT();
  const moTaLoi = useMoTaLoi();
  return (
    <div role="alert" className="rounded-lg border border-danger-500 bg-danger-50 p-3 text-start text-xs text-danger-700">
      <p className="font-medium">{t('troLyAiHoi.loiNguonTieuDe')}</p>
      <p className="mt-1 break-words leading-relaxed">{moTaLoi(loi)}</p>
      <p className="mt-1 leading-relaxed text-content-secondary">{t('troLyAiHoi.loiNguonGoiY')}</p>
      <Button size="sm" variant="ghost" className="mt-2" onClick={onThuLai}>
        {t('chung.thuLai')}
      </Button>
    </div>
  );
}

/**
 * Hỏi đáp AI (`/tro-ly-ai`) — dựng như một cuộc hỏi–đáp (port `AskDataView.tsx` native).
 *
 * Ô soạn ghim đáy; lời văn đứng trước bảng (bảng 200 dòng là đúng dữ liệu, vô dụng về câu trả lời). `?q=` cho màn khác
 * gửi sẵn một câu hỏi — điền sẵn chứ KHÔNG tự gửi; `?cv=` mở thẳng một đoạn chat đã lưu.
 */
export function HoiDapView() {
  const { t, locale } = useT();
  const toast = useToast();
  const moTaLoi = useMoTaLoi();
  const [params] = useSearchParams();

  const [question, setQuestion] = useState(() => params.get('q') ?? '');

  // ── ĐỌC THÀNH TIẾNG ──
  const dsGiong = useDanhSachGiong();
  const [batDoc, setBatDoc] = useState(() => nho('ask.doc') === '1');
  const [giong, setGiong] = useState<string | null>(() => nho('ask.giong') || null);
  // Mã đã lưu có thể KHÔNG còn trong danh sách (máy chủ đổi bộ giọng) — kiểm lại thay vì tin; chưa chọn thì giọng đầu.
  const giongDung = (giong && dsGiong.some((g) => g.ma === giong) ? giong : null) ?? dsGiong[0]?.ma ?? null;
  const doc = useDocTo();
  // Đọc được từ trong callback SSE: tắt loa GIỮA lượt phải có tác dụng ngay.
  const batDocRef = useRef(batDoc);
  useEffect(() => {
    batDocRef.current = batDoc;
  }, [batDoc]);

  // Công tắc trình bày: nhớ trong TRÌNH DUYỆT NÀY — sở thích đọc của một người, không phải cấu hình của nguồn.
  const [bieuDo, setBieuDo] = useState(() => nho('ask.bieuDo', 'tu_dong') || 'tu_dong');
  const [mucVan, setMucVan] = useState(() => nho('ask.mucVan', 'phan_tich') || 'phan_tich');
  const [doDai, setDoDai] = useState(() => nho('ask.doDai', 'vua'));

  const sources = useDanhSachNguon();
  const dsNguon = useMemo(() => sources.data ?? [], [sources.data]);

  // ── NGUỒN LÀ MỘT LỰA CHỌN, KHÔNG PHẢI HỆ QUẢ CỦA THỨ TỰ SẮP XẾP ── và nhiều nguồn là nhiều lượt tra riêng: một câu
  // SQL không chạm được hai CSDL, câu trả lời ghi rõ con số nào của nguồn nào.
  const [nguonDaChon, setNguonDaChon] = useState<string[]>(() => nhoDs(KHOA_NGUON, KHOA_NGUON_CU));
  const nguonHoi = nguonDemHoi(nguonDaChon, dsNguon);
  const sourceId = nguonHoi[0] ?? '';

  const health = useSucKhoeTroLy();
  // Mô hình cho lượt tới. Rỗng = mặc định. Giữ ở state trang, KHÔNG ghi lên máy chủ: không đụng tới lượt của người khác.
  const [model, setModel] = useState('');

  const oLichSu = useRef<LuotLichSu[]>([]);
  const noiTieng = useMemo<TuyChonLuotHoi>(
    () => ({
      // Kiểm `batDoc` Ở ĐÂY: tắt loa giữa lượt thì máy chủ vẫn gửi nốt các mẩu đã tổng hợp.
      onTieng: (stt, goi) => {
        if (batDocRef.current) doc.them(stt, goi);
      },
      onXongTieng: () => doc.xong(),
      // `turns` chỉ có lượt trong phiên này; mở lại một đoạn cũ rồi hỏi tiếp mà không có quá khứ thì đại từ không giải được.
      lichSuCu: () => oLichSu.current,
    }),
    [doc],
  );
  const { turns, hoi, dung, dangChay, xoaHet } = useLuotHoi(noiTieng);

  // ── LỊCH SỬ: LƯU KHI HỎI, KHÔNG PHẢI KHI MỞ MÀN ── đoạn rỗng sinh ra do mở màn sẽ nằm mãi trong danh sách.
  const [cvId, setCvId] = useState<string | null>(() => params.get('cv'));
  // ID tin nhắn CHÍNH PHIÊN NÀY vừa ghi — `turns` đang vẽ chúng; chống hiện HAI LẦN bằng ID, không bằng nội dung.
  const [daGhi, setDaGhi] = useState<Set<string>>(new Set());
  const [canXoa, setCanXoa] = useState<MucDoan | null>(null);

  const chuyenDoan = (moi: string | null) => {
    if (moi !== cvId) {
      xoaHet();
      setDaGhi(new Set());
    }
    setCvId(moi);
  };

  const chuaSanSang = dsNguon.filter((x) => nguonHoi.includes(x.id) && x.status !== 'ready');

  const doiNguon = (ds: string[]) => {
    setNguonDaChon(ds);
    ghiNhoDs(KHOA_NGUON, ds);
    // ── ĐỔI LỰA CHỌN THÌ ĐÓNG ĐOẠN ĐANG MỞ, VÀ NÓI RA ── nhãn nguồn trong một đoạn chỉ có nghĩa trong phạm vi sinh ra
    // nó. Đóng im lặng thì người dùng thấy cuộc trò chuyện biến mất; mở lại đoạn từ danh sách sẽ khôi phục lựa chọn.
    if (cvId) toast.info(t('troLyAiHoi.doiNguonDongDoan'));
    chuyenDoan(null);
  };

  const lichSu = useHoiThoaiTheoNguon(nguonHoi);
  const xoa = useXoaHoiThoai();
  const dangMo = useMoHoiThoai(cvId);
  const ghi = useGhiHoiThoai();

  // Nạp sau khi dựng: `dangMo` về sau lần dựng đầu, và ref phải mang bản mới nhất khi người dùng bấm gửi.
  useEffect(() => {
    oLichSu.current = lichSuTuTinNhan(dangMo.data?.messages ?? [], daGhi);
  }, [dangMo.data, daGhi]);

  // Mở một đoạn qua `?cv=`: khôi phục lựa chọn nguồn của nó khi đã có danh sách nguồn — cùng luật với `moDoan`.
  const daKhoiPhuc = useRef(false);
  useEffect(() => {
    if (daKhoiPhuc.current || !dangMo.data || !dsNguon.length) return;
    daKhoiPhuc.current = true;
    const phamVi = (dangMo.data.scopeIds ?? []).filter((id) => dsNguon.some((x) => x.id === id));
    if (phamVi.length) {
      setNguonDaChon(phamVi);
      ghiNhoDs(KHOA_NGUON, phamVi);
    }
  }, [dangMo.data, dsNguon]);

  /** Mở một đoạn cũ — và KHÔI PHỤC lựa chọn nguồn của nó (nửa còn lại của luật "đổi nguồn thì đóng đoạn"). */
  const moDoan = (id: string) => {
    const cv = lichSu.data?.find((c) => c.id === id);
    const phamVi = (cv?.scopeIds ?? []).filter((x) => dsNguon.some((s) => s.id === x));
    if (phamVi.length && phamVi.join(',') !== nguonHoi.join(',')) {
      setNguonDaChon(phamVi);
      ghiNhoDs(KHOA_NGUON, phamVi);
    }
    chuyenDoan(id);
  };

  // Nghe câu hỏi: ghi vào ô soạn thay vì gửi ngay — nhận dạng sai chính tả là chuyện thường.
  const coNghe = useCoNghe();
  const nghe = useNghe(setQuestion);

  const cuoi = useRef<HTMLDivElement>(null);
  useEffect(() => {
    cuoi.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns, dangMo.data]);

  const send = async (q = question) => {
    if (!sourceId || !q.trim()) return;
    // MỘT LƯỢT MỘT LẦN: nút gửi khoá khi đang chạy nhưng phím Enter thì không — gửi lần hai giữa chừng cho hai dòng SSE
    // chồng nhau, dòng sau cắt ngang tiếng đang đọc.
    if (dangChay) return;
    setQuestion('');
    // Lượt mới: im ngay và đặt lại con trỏ đọc về 0, nếu không bộ đọc bỏ qua phần đầu lượt mới.
    doc.dung();

    let doan = cvId;
    if (!doan) {
      try {
        doan = await ghi.taoDoan(nguonHoi);
        setCvId(doan);
      } catch {
        // Lưu hỏng KHÔNG chặn việc hỏi: câu trả lời là thứ người dùng tới đây để lấy, lưu lại là tiện ích.
        doan = null;
      }
    }

    const kq = await hoi({
      question: q,
      sourceIds: nguonHoi,
      conversationId: doan ?? undefined,
      maxRows: 200,
      model: model || undefined,
      mucVan,
      doDai,
      // Chỉ gửi khi ĐANG bật loa (mỗi câu ~470 KB đi qua dây). `auto` khi chưa có danh sách giọng — gửi `undefined` thì
      // máy chủ không tổng hợp gì, im lặng, không lỗi.
      giong: batDoc ? (giongDung ?? 'auto') : undefined,
      tiengDan: true,
      // Không gửi khi `tu_dong`: để máy chủ giữ phép đoán mặc định.
      bieuDo: bieuDo === 'tu_dong' ? undefined : bieuDo,
    });
    if (!doan || !kq?.answer) return;

    try {
      const [mUser, mBot] = await ghi.ghiLuot(doan, q, kq.answer, {
        sql: kq.sql,
        rowCount: kq.rowCount,
        kind: kq.kind,
        ...(kq.traceId ? { traceId: kq.traceId } : {}),
        // Số liệu để lượt đọc lại vẽ được biểu đồ và bảng như lượt đang chạy.
        ...(goiSoLieu(kq) ?? {}),
      });
      setDaGhi((p) => new Set(p).add(mUser).add(mBot));
    } catch {
      /* lưu hỏng thì thôi — xem chú thích trên */
    }
  };

  const llmOff = health.data && !health.data.llmConfigured;
  const troiTrong = !dangMo.data?.messages?.length && !turns.length;
  const dsDoan = lichSu.data?.map((c) => ({ id: c.id, ten: c.title || t('troLyAiHoi.doanHoiMacDinh') }));

  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <PageHeader
        breadcrumb={[{ label: t('nav.nhom.troLiAi') }, { label: t('nav.muc.hoiDapAi') }]}
        title={t('troLyAiHoi.tieuDe')}
        subtitle={t('troLyAiHoi.moTa')}
      />

      <div className="flex min-h-0 flex-1 gap-3">
        <DanhSachDoan
          nhanMoi={t('troLyAiHoi.doanHoiMoi')}
          moiDuoc={!!sourceId}
          onMoi={() => chuyenDoan(null)}
          ds={sources.isError || !sourceId ? [] : dsDoan}
          dangChon={cvId}
          dangTai={!!sourceId && lichSu.isPending}
          rong={
            sources.isError ? (
              <LoiNguon loi={sources.error} onThuLai={() => void sources.refetch()} />
            ) : !sourceId ? (
              t('troLyAiHoi.chuaCoNguon')
            ) : lichSu.isError ? (
              moTaLoi(lichSu.error)
            ) : (
              t('troLyAiHoi.chuaCoDoan')
            )
          }
          onChon={moDoan}
          onXoa={setCanXoa}
          nhanXoa={(ten) => t('troLyAiHoi.xoaDoanHoi', { ten })}
          chan={t('troLyAiHoi.lichSuTheoNguon')}
        />

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          <header className="flex min-h-14 shrink-0 flex-wrap items-center justify-between gap-3 border-b border-default px-4 py-2">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                <RobotOutlined />
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-content-primary">{t('troLyAiHoi.tieuDe')}</p>
                <p className="truncate text-xs text-content-secondary">{t('troLyAiHoi.phuDe')}</p>
              </div>
            </div>
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              {/* Giọng đọc ở header, không ở thanh soạn: nó là cách người ta NHẬN câu trả lời, đặt một lần rồi giữ. */}
              <ChonGiong
                danhSach={dsGiong}
                giong={giongDung}
                onDoiGiong={(v) => {
                  setGiong(v);
                  ghiNho('ask.giong', v);
                }}
                bat={batDoc}
                onBat={(v) => {
                  setBatDoc(v);
                  // Tắt thì im NGAY. Bật có hiệu lực từ lượt SAU — âm thanh lượt này không được tổng hợp.
                  if (!v) doc.dung();
                  ghiNho('ask.doc', v ? '1' : '0');
                }}
                dangDoc={doc.dangDoc}
                dangTam={doc.dangTam}
                onTam={doc.bamTam}
              />
              {dsNguon.length > 0 && (
                <Select<string[]>
                  mode="multiple"
                  maxTagCount="responsive"
                  size="small"
                  allowClear={false}
                  aria-label={t('troLyAiHoi.nguonDuLieu')}
                  value={nguonHoi}
                  onChange={(v) => doiNguon(v)}
                  className="w-[260px] max-w-full"
                  options={dsNguon.map((x) => ({
                    value: x.id,
                    label: x.name,
                    // Trạng thái ngay trong danh sách: chọn một nguồn chưa `ready` là chọn một nguồn sẽ trả lời rỗng.
                    title: x.status !== 'ready' ? t('troLyAiHoi.chuaSanSang', { ten: x.name }) : x.name,
                  }))}
                />
              )}
              {/* Đếm chứ không nêu tên: năm tên nguồn là một dòng dài hơn cả thanh tiêu đề. */}
              {chuaSanSang.length > 0 && (
                <Badge variant="warning">
                  {chuaSanSang.length === 1
                    ? t('troLyAiHoi.nguonChuaSanSang')
                    : t('troLyAiHoi.nhieuNguonChuaSanSang', { n: soVN(chuaSanSang.length, locale) })}
                </Badge>
              )}
            </div>
          </header>

          {/* Các lượt hỏi: phần DUY NHẤT tự cuộn. */}
          <div className="flex-1 overflow-y-auto px-4 py-5 lg:px-6" aria-live="polite">
            <div className="mx-auto flex min-h-full w-full max-w-5xl flex-col space-y-6">
              {dangMo.isError ? <p className="text-xs text-danger-700">{moTaLoi(dangMo.error)}</p> : null}
              {/* Lượt ĐÃ LƯU của đoạn đang mở. `LoiVan` chứ không `<p>`: lời văn lưu NGUYÊN VĂN markdown. */}
              {dangMo.data?.messages
                ?.filter((m) => !daGhi.has(m.id))
                .map((m) =>
                  m.author === 'user' ? (
                    <BongHoi key={m.id}>{m.text}</BongHoi>
                  ) : (
                    <div key={m.id}>
                      <LoiVan text={m.text} className="text-sm leading-relaxed text-content-primary" />
                      <KhoiSoLieuDaLuu payload={m.payload} />
                    </div>
                  ),
                )}

              {turns.map((turn) => (
                <LuotHoi key={turn.id} turn={turn} sourceId={sourceId} onHoiTiep={(q) => void send(q)} />
              ))}

              {troiTrong ? (
                <div className="flex flex-1 flex-col items-center justify-center text-center">
                  <div className="my-auto max-w-md space-y-3 p-6">
                    <span className="mx-auto flex size-12 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                      <RobotOutlined className="text-xl" />
                    </span>
                    <h3 className="text-base font-semibold text-content-primary">{t('troLyAiHoi.tieuDe')}</h3>
                    {sources.isError ? (
                      <LoiNguon loi={sources.error} onThuLai={() => void sources.refetch()} />
                    ) : sources.isPending ? null : !sourceId ? (
                      <p className="text-xs leading-relaxed text-content-secondary">{t('troLyAiHoi.chuaCoNguon')}</p>
                    ) : null}
                    {/* Gợi ý lấy từ nguồn đầu trong lựa chọn: câu MẪU để bấm thử, trộn của năm nguồn thì không đọc được. */}
                    <GoiY sourceId={sourceId} onPick={(q) => void send(q)} disabled={!sourceId} />
                  </div>
                </div>
              ) : null}
              <div ref={cuoi} />
            </div>
          </div>

          <ThanhSoan
            question={question}
            onQuestion={setQuestion}
            onGui={() => void send()}
            onDung={dung}
            dangChay={dangChay}
            placeholder={t('troLyAiHoi.oSoanPlaceholder')}
            guiDuoc={!!sourceId}
            lyDoKhoa={t('troLyAiHoi.chuaChonNguon')}
            tuyChon={{
              mucVan,
              doDai,
              bieuDo,
              onMucVan: (v) => {
                setMucVan(v);
                ghiNho('ask.mucVan', v);
              },
              onDoDai: (v) => {
                setDoDai(v);
                ghiNho('ask.doDai', v);
              },
              onBieuDo: (v) => {
                setBieuDo(v);
                ghiNho('ask.bieuDo', v);
              },
            }}
            model={model}
            onModel={setModel}
            hienMacDinh
            mic={coNghe ? nghe : undefined}
            canhBao={
              llmOff ? (
                <p className="mb-2 flex items-start gap-1.5 text-xs text-warning-700">
                  <WarningOutlined className="mt-0.5 shrink-0 text-xs" />
                  {t('troLyAiHoi.llmOff', { model: tenCongKhaiMacDinh(health.data?.model, health.data?.models) })}
                </p>
              ) : null
            }
            goiY={t('troLyAiHoi.enterDeGui')}
          />
        </div>
      </div>

      <HopXacNhan
        open={!!canXoa}
        onClose={() => {
          setCanXoa(null);
          xoa.reset();
        }}
        tieuDe={t('troLyAiHoi.xacNhanXoaTieuDe', { ten: canXoa?.ten ?? '' })}
        than={t('troLyAiHoi.xacNhanXoaNoiDung')}
        nhanXacNhan={t('troLyAiHoi.xoaDoanNut')}
        dangChay={xoa.isPending}
        loi={xoa.error}
        onXacNhan={() => {
          if (!canXoa) return;
          xoa.mutate(canXoa.id, {
            onSuccess: () => {
              if (cvId === canXoa.id) chuyenDoan(null);
              setCanXoa(null);
            },
          });
        }}
      />
    </div>
  );
}
